"use client";

import type {
  CareGiverLeadFormData,
  CareGiverLeadFormResponse,
} from "~www/types/forms/care-giver-lead-form";
import type { LeadResponse } from "~www/types/forms/lead-response";
import type { PageBuilderOffice } from "~www/types/office";
import type { JobSelectionState } from "./JobSection";
import type { OfficeSearchState } from "./OfficeSearchSection";

import { CareerFormSubmitEvent, track } from "@homeinstead/analytics";
import { useHiringOfficeSearch } from "@homeinstead/geolocation/hooks";
import Loading from "@homeinstead/icons/Loading";
import { mapNearbyOrganizationsToOfficeOptions } from "@homeinstead/marketing-api";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import { Button } from "@homeinstead/ui/components/Button";
import { Checkbox } from "@homeinstead/ui/components/Checkbox";
import { FormError } from "@homeinstead/ui/components/FormError";
import { Input } from "@homeinstead/ui/components/Input";
import { Link } from "@homeinstead/ui/components/Link";
import { PhoneInput } from "@homeinstead/ui/components/PhoneInput";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useActionState, useMemo, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";
import { submitCareGiverForm } from "~www/actions/submitCareGiverForm";
import { useLocale } from "~www/components/LocaleProvider";
import { useOfficeJobs } from "~www/hooks/useOfficeJobs";
import { careGiverLeadFormSchema } from "./care-giver-lead-form-schema";
import { getCheckboxFields, textFields } from "./fields";
import { JobSection } from "./JobSection";
import { OfficeSearchSection } from "./OfficeSearchSection";
import { PrivacyPolicyDisclaimer } from "./PrivacyPolicyDisclaimer";
import { CareGiverLeadFormThankYou } from "./ThankYou";

interface CareGiverLeadFormProps
  extends React.FormHTMLAttributes<HTMLFormElement> {
  isLastStep: boolean;
  handleNext: () => void;
  office?: PageBuilderOffice;
  defaultJobId?: string;
}

const CareGiverLeadForm = ({
  className,
  isLastStep,
  handleNext,
  office,
  defaultJobId,
  ...props
}: CareGiverLeadFormProps) => {
  const locale = useLocale();
  const siteUrl = locale.metadata.canonicalOrigin;

  const methods = useForm<CareGiverLeadFormData>({
    defaultValues: {
      step1: { Role: "caregiver" },
      step2: {
        BusinessInEnglish: false,
        CanWorkInUS: false,
        Email: "",
        FirstName: "",
        jobID: defaultJobId ?? "",
        LastName: "",
        OptInEmail: true,
        OptInSms: false,
        organizationNumber: office?.officeID ? Number(office?.officeID) : 0,
        Phone: "",
        postalCode: office?.address.postalCode ?? "",
        userEnteredLocation: office?.address.postalCode ?? "",
      },
    },
    mode: "onChange",
    resolver: zodResolver(careGiverLeadFormSchema),
  });

  const {
    register,
    control,
    formState: { errors, isValid },
  } = methods;

  const officeNumber = useWatch({ control, name: "step2.organizationNumber" });
  const userEnteredLocation = useWatch({
    control,
    name: "step2.userEnteredLocation",
  });
  const postalCode = useWatch({ control, name: "step2.postalCode" });

  const {
    data: hiringOfficeSearch,
    isFetching: isFetchingHiringOffices,
    error: hiringOfficeSearchError,
  } = useHiringOfficeSearch({
    enabled: Boolean(userEnteredLocation),
    location: userEnteredLocation,
  });

  const officeOptions = useMemo(
    () =>
      mapNearbyOrganizationsToOfficeOptions(
        hiringOfficeSearch?.organizations ?? [],
      ),
    [hiringOfficeSearch?.organizations],
  );

  const {
    data: locationCareers,
    isFetching: isFetchingLocationCareers,
    error: errorOnLocationCareers,
  } = useOfficeJobs({ officeNumber });

  const [officeSelectOpen, setOfficeSelectOpen] = useState(false);
  const [jobSelectOpen, setJobSelectOpen] = useState(false);

  const hasLocalFranchise = Boolean(office);

  const officeSearchState: OfficeSearchState = useMemo(() => {
    if (hasLocalFranchise) {
      return "local";
    }
    if (!userEnteredLocation) {
      return "idle";
    }
    if (isFetchingHiringOffices) {
      return "loading";
    }
    if (hiringOfficeSearchError) {
      return "error";
    }
    if (officeOptions.length === 0) {
      return "no-offices";
    }
    return "has-offices";
  }, [
    hasLocalFranchise,
    userEnteredLocation,
    isFetchingHiringOffices,
    hiringOfficeSearchError,
    officeOptions.length,
  ]);

  const jobSelectionState: JobSelectionState = useMemo(() => {
    // If the location selection isn't ready to show jobs, short-circuit
    if (officeSearchState !== "local") {
      if (officeSearchState !== "has-offices") {
        return "idle";
      }

      if (!officeNumber) {
        return "idle";
      }
      if (isFetchingLocationCareers) {
        return "loading";
      }
      if (errorOnLocationCareers) {
        return "error";
      }
    }

    if (!locationCareers) {
      return "idle";
    }

    // Make sure there is at least one job in the array
    return locationCareers && locationCareers.length > 0
      ? "has-jobs"
      : "no-jobs";
  }, [
    officeSearchState,
    officeNumber,
    isFetchingLocationCareers,
    errorOnLocationCareers,
    locationCareers,
  ]);

  // Close job dropdown whenever location becomes invalid
  React.useEffect(() => {
    if (officeSearchState !== "has-offices") {
      // TODO stop suppressing this warning (don't replicate this!)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset job dropdown when office search is no longer valid
      setJobSelectOpen(false);
    }
    if (jobSelectionState === "no-jobs" || jobSelectionState === "error") {
      methods.setValue("step2.jobID", "", {
        shouldValidate: true,
      });
    }
  }, [officeSearchState, jobSelectionState, methods]);

  const [state, submitFormAction, isPending] = useActionState<
    LeadResponse<CareGiverLeadFormResponse>,
    FormData
  >(submitCareGiverForm, null);

  if (state?.ok) {
    const redirectURL = state.data?.jobApplicationUrl;
    if (redirectURL) {
      track(
        EventName.CAREER_FORM_SUBMIT,
        // TODO: fix
        CareerFormSubmitEvent({
          franchiseNumber: officeNumber.toString(),
          result: "success",
          tags: state.tags,
        }),
      );

      // defer redirect until next tick to let analytics fire
      setTimeout(() => window.location.assign(redirectURL), 30);

      return (
        <div className="p-8 text-center text-new-beginnings-base">
          <p className="font-semibold text-md">
            Redirecting you to the job application…
          </p>
        </div>
      );
    }
    return <CareGiverLeadFormThankYou />;
  } else if (state !== null && !state?.ok) {
    track(
      EventName.CAREER_FORM_SUBMIT,
      CareerFormSubmitEvent({
        franchiseNumber: officeNumber.toString(),
        result: state?.validationErrors ? "validation-error" : "server-error",
        tags: state.tags,
      }),
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        action={submitFormAction}
        className={cn("care-giver-lead-form", "px-4 xs:px-12 py-4", className)}
        {...props}
      >
        <div className="flex w-full flex-col gap-4 md:w-[424px]">
          <h2 className="font-bold text-lg text-new-beginnings-base">
            Apply to be a caregiver
          </h2>

          <Input type="hidden" {...register("step1.Role")} value="caregiver" />

          {textFields.map(([field, label]) => {
            const name = `step2.${field}` as const;
            return (
              <div key={field}>
                <Input
                  id={field}
                  placeholder={label}
                  type={field === "Email" ? "email" : "text"}
                  {...register(name)}
                  className="bg-white text-base text-new-beginnings-base"
                />
                <FormError.span className="text-error">
                  {errors.step2?.[field]?.message}
                </FormError.span>
              </div>
            );
          })}

          <PhoneInput<CareGiverLeadFormData>
            className="bg-white text-base text-new-beginnings-base"
            errorClassName="text-error"
            fieldName="step2.Phone"
          />

          {officeSearchState === "local" ? (
            <>
              <input
                type="hidden"
                value={officeNumber}
                {...register("step2.organizationNumber")}
              />
              <input
                type="hidden"
                value={postalCode}
                {...register("step2.postalCode")}
              />
              <input
                type="hidden"
                value={userEnteredLocation}
                {...register("step2.userEnteredLocation")}
              />
            </>
          ) : (
            <OfficeSearchSection
              officeOptions={officeOptions}
              officeSearchState={officeSearchState}
              officeSelectOpen={officeSelectOpen}
              setOfficeSelectOpen={setOfficeSelectOpen}
            />
          )}

          <JobSection
            isOpen={jobSelectOpen}
            jobSelectionState={jobSelectionState}
            locationCareers={locationCareers}
            setIsOpen={setJobSelectOpen}
          />

          {getCheckboxFields(locale.metadata.code).map(([field, label]) => {
            const name = `step2.${field}` as const;
            return (
              <Controller
                control={control}
                key={field}
                name={name}
                render={({ field: ctrl }) => (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-start gap-2">
                      <Checkbox
                        checked={ctrl.value}
                        id={name}
                        name={name}
                        onChange={ctrl.onChange}
                      />
                      <label
                        className="text-new-beginnings-base text-xs"
                        htmlFor={name}
                      >
                        {label}
                        {field === "OptInSms" ? (
                          <>
                            <Link
                              className="text-xs underline hover:no-underline"
                              href={`${siteUrl}/legal/privacy-policy`}
                              size="inline"
                              variant="plain"
                            >
                              Privacy Policy
                            </Link>
                            .
                          </>
                        ) : null}
                      </label>
                    </div>
                    {errors.step2?.[field] && (
                      <FormError.span className="text-error">
                        {errors.step2[field]?.message}
                      </FormError.span>
                    )}
                  </div>
                )}
              />
            );
          })}
        </div>

        <Button
          className="mt-4 h-16 w-full rounded-full"
          disabled={isPending || !isValid}
          loading={isPending}
          onClick={isLastStep ? undefined : handleNext}
          type={isLastStep ? "submit" : "button"}
        >
          {isPending ? (
            <Loading className="h-8 w-8 fill-new-beginnings-base" />
          ) : isLastStep ? (
            "Submit"
          ) : (
            "Next"
          )}
        </Button>

        {isLastStep && <PrivacyPolicyDisclaimer />}
      </form>
    </FormProvider>
  );
};

export default CareGiverLeadForm;
